import { useCallback, useEffect, useState } from "react";
import { ClipboardList, UserPlus, Trash2, MessageCircle, CheckCircle2, ChevronDown, Send } from "lucide-react";
import { toast } from "sonner";
import { api, coachHeaders } from "../lib/api";
import { openWhatsApp, openWhatsAppTo, waNumero } from "./ShareWhatsApp";
import { nomCat } from "../lib/categorie";

const frDate = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso ?? "") ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const CLUB = "🏟️ Cette fiche est demandée par le club USC Jemappes.";
const lienFiche = () => `👉 ${window.location.origin}/fiche`;

export const envoyerFiche = () => openWhatsApp([
  "Bonjour à tous,",
  "Merci de remplir la fiche de renseignements de votre enfant (contacts, urgence, infos médicales). Cela prend 3 minutes et reste confidentiel.",
  CLUB,
  "",
  "📝 *FICHE DE RENSEIGNEMENTS*",
  lienFiche(),
].join("\n"));

const relanceGroupe = (manquants) => openWhatsApp([
  "Bonjour à tous,",
  `Il nous manque encore la fiche de renseignements de : ${manquants.map((j) => nomCat(j)).join(", ")}.`,
  "Merci aux parents concernés de la remplir (3 minutes, confidentiel).",
  CLUB,
  "",
  "📝 *FICHE DE RENSEIGNEMENTS*",
  lienFiche(),
].join("\n"));

const lienPerso = (j) => `👉 ${window.location.origin}/fiche?${j.fiche_token ? new URLSearchParams({ t: j.fiche_token }) : new URLSearchParams({ nom: j.nom, prenom: j.prenom })}`;

const texteParent = (j) => [
  `Bonjour${j.responsable ? ` ${j.responsable}` : ""},`,
  j.fiche_date
    ? `Voici le lien pour vérifier et mettre à jour la fiche de renseignements de ${j.prenom}. Les informations déjà connues sont pré-remplies.`
    : `Pourriez-vous remplir la fiche de renseignements de ${j.prenom} ? Cela prend 3 minutes et reste confidentiel.`,
  CLUB,
  "",
  "📝 *FICHE DE RENSEIGNEMENTS*",
  lienPerso(j),
  "Ce lien est personnel : merci de ne pas le partager.",
  "",
  "Merci et à bientôt au club !",
].join("\n");

// Message personnel au parent (lien pré-rempli avec le nom de l'enfant) ; sans numéro, WhatsApp laisse choisir le contact.
export const envoyerFicheParent = (j) => (waNumero(j.telephone) ? openWhatsAppTo(j.telephone, texteParent(j)) : openWhatsApp(texteParent(j)));
const relanceParent = envoyerFicheParent;

const texteEncadrant = (s) => [
  `Bonjour ${s.nom},`,
  s.fiche_date
    ? "Voici le lien pour vérifier et mettre à jour ta fiche de renseignements d'encadrant. Les informations déjà connues sont pré-remplies."
    : "Pourrais-tu remplir ta fiche de renseignements d'encadrant (coordonnées, urgence, infos médicales) ? Cela prend 2 minutes et reste confidentiel.",
  CLUB,
  "",
  "📝 *FICHE ENCADRANT*",
  `👉 ${window.location.origin}/fiche-encadrant?${new URLSearchParams({ t: s.fiche_token ?? "" })}`,
  "Ce lien est personnel : merci de ne pas le partager.",
].join("\n");

export const envoyerFicheEncadrant = (s) => (waNumero(s.telephone) ? openWhatsAppTo(s.telephone, texteEncadrant(s)) : openWhatsApp(texteEncadrant(s)));

const EncadrantsManquants = ({ staff }) => {
  const manquants = staff.filter((s) => !s.fiche_date);
  if (staff.length === 0) return null;
  if (manquants.length === 0) return <p data-testid="fiches-enc-manquantes-none" className="mt-3 flex items-center gap-1 text-sm text-lime"><CheckCircle2 className="h-4 w-4" />Tous les encadrants ont envoyé leur fiche.</p>;
  return (
    <div data-testid="fiches-enc-manquantes" className="mt-3 rounded-xl border border-signal/50 bg-signal/5 p-3">
      <p className="text-sm font-semibold text-chalk"><span data-testid="fiches-enc-manquantes-count">{manquants.length}</span> encadrant{manquants.length > 1 ? "s" : ""} sans fiche</p>
      <ul className="mt-2 divide-y divide-line">
        {manquants.map((s) => (
          <li key={s.id} data-testid={`fiche-enc-manquante-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <p className="min-w-0 text-sm text-chalk"><span className="font-semibold">{s.nom}</span>{s.fonction && <span className="ml-1 text-dim">· {s.fonction}</span>}</p>
            <button type="button" data-testid={`fiche-relance-enc-${s.id}`} onClick={() => envoyerFicheEncadrant(s)} className="inline-flex items-center gap-1 rounded-full border border-[#25D366] px-3 py-1 text-sm text-[#25D366] hover:bg-[#25D366] hover:text-ink"><Send className="h-3.5 w-3.5" />Relancer{waNumero(s.telephone) ? ` (…${s.telephone.replace(/\D/g, "").slice(-2)})` : ""}</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AjoutTel = ({ j, code, onSaved }) => {
  const [tel, setTel] = useState("");
  const [busy, setBusy] = useState(false);
  const envoyer = async () => {
    if (!waNumero(tel)) { toast.error("Numéro de téléphone invalide"); return; }
    const maj = { ...j, telephone: tel.trim() };
    relanceParent(maj);
    setBusy(true);
    try {
      await api.put(`/joueurs/${j.id}`, maj, coachHeaders(code));
      toast.success(`Numéro enregistré pour ${j.prenom}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement du numéro impossible"); } finally { setBusy(false); }
  };
  return (
    <div className="flex w-full gap-2 sm:w-auto">
      <input data-testid={`fiche-ajout-tel-${j.id}`} type="tel" value={tel} onChange={(e) => setTel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") envoyer(); }} placeholder="Tél. du parent à ajouter"
        className="min-w-0 flex-1 rounded-full border border-signal/60 bg-ink px-3 py-1 text-sm text-chalk outline-none focus:border-lime sm:w-44" />
      <button type="button" data-testid={`fiche-ajout-tel-envoyer-${j.id}`} disabled={busy} onClick={envoyer} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#25D366] px-3 py-1 text-sm text-[#25D366] hover:bg-[#25D366] hover:text-ink disabled:opacity-50"><Send className="h-3.5 w-3.5" />Enregistrer et envoyer</button>
    </div>
  );
};

const Manquantes = ({ joueurs, code, onChange }) => {
  const [open, setOpen] = useState(true);
  const manquants = joueurs.filter((j) => !j.fiche_date);
  if (joueurs.length === 0) return null;
  if (manquants.length === 0) return <p data-testid="fiches-manquantes-none" className="mt-3 flex items-center gap-1 text-sm text-lime"><CheckCircle2 className="h-4 w-4" />Tous les enfants de l'effectif ont envoyé leur fiche.</p>;
  return (
    <div data-testid="fiches-manquantes" className="mt-3 rounded-xl border border-signal/50 bg-signal/5 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" data-testid="fiches-manquantes-toggle" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-sm font-semibold text-chalk">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          <span data-testid="fiches-manquantes-count">{manquants.length}</span>&nbsp;enfant{manquants.length > 1 ? "s" : ""} sans fiche
        </button>
        <button type="button" data-testid="fiches-relance-groupe" onClick={() => relanceGroupe(manquants)} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-3 py-1.5 text-sm font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"><MessageCircle className="h-4 w-4" />Relancer le groupe</button>
      </div>
      {open && (
        <ul className="mt-2 divide-y divide-line">
          {manquants.map((j) => (
            <li key={j.id} data-testid={`fiche-manquante-${j.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <p className="min-w-0 text-sm text-chalk"><span className="font-semibold">{nomCat(j)}</span>{j.responsable && <span className="ml-1 text-dim">· {j.resp1_lien ? `${j.resp1_lien} ` : ""}{j.responsable}</span>}</p>
              {waNumero(j.telephone)
                ? <button type="button" data-testid={`fiche-relance-parent-${j.id}`} onClick={() => relanceParent(j)} className="inline-flex items-center gap-1 rounded-full border border-[#25D366] px-3 py-1 text-sm text-[#25D366] hover:bg-[#25D366] hover:text-ink"><Send className="h-3.5 w-3.5" />Écrire au parent (…{j.telephone.replace(/\D/g, "").slice(-2)})</button>
                : <><span className="text-xs text-dim md:hidden">Pas de numéro : ajoutez-le sur ordinateur</span><span className="hidden md:contents"><AjoutTel j={j} code={code} onSaved={onChange} /></span></>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FicheRow = ({ fi, onAdd, onDelete, busy }) => (
  <li data-testid={`fiche-row-${fi.id}`} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 ${fi.vue ? "border-line" : "border-lime bg-lime/10"}`}>
    <div className="min-w-0">
      <p className="font-semibold text-chalk">{fi.prenom} {fi.nom.toUpperCase()}
        {fi.type === "encadrant" && <span data-testid={`fiche-badge-encadrant-${fi.id}`} className="ml-2 rounded-full border border-lime px-2 py-0.5 font-mono text-[10px] uppercase text-lime">Encadrant{fi.fonction ? ` · ${fi.fonction}` : ""}</span>}
        {!fi.vue && <span className="ml-2 rounded-full bg-lime px-2 py-0.5 font-mono text-[10px] uppercase text-ink">Nouvelle</span>}</p>
      <p className="text-xs text-dim">Reçue le {frDate(fi.recue_le)}{fi.responsable ? ` · ${fi.responsable}` : ""}{fi.date_naissance ? ` · né(e) le ${frDate(fi.date_naissance)}` : ""}</p>
    </div>
    {fi.statut === "a_traiter" ? (
      <div className="hidden gap-2 md:flex">
        <button type="button" data-testid={`fiche-add-${fi.id}`} disabled={busy} onClick={() => onAdd(fi)} className="inline-flex items-center gap-1 rounded-full bg-lime px-3 py-1.5 text-sm font-semibold text-ink disabled:opacity-50"><UserPlus className="h-4 w-4" />Ajouter à l'effectif</button>
        <button type="button" data-testid={`fiche-delete-${fi.id}`} disabled={busy} onClick={() => onDelete(fi)} aria-label="Supprimer la fiche" className="rounded-full border border-line p-2 text-chalk hover:border-signal hover:text-signal"><Trash2 className="h-4 w-4" /></button>
      </div>
    ) : <span className="inline-flex items-center gap-1 text-xs text-lime"><CheckCircle2 className="h-4 w-4" />{fi.type === "encadrant" ? "Fiche encadrant mise à jour" : "Ajoutée à l'effectif"}</span>}
  </li>
);

export const FichesRecues = ({ code, onCount, onIntegree }) => {
  const [fiches, setFiches] = useState([]);
  const [joueurs, setJoueurs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const [histo, setHisto] = useState(false);
  const load = useCallback(async () => {
    api.get("/joueurs", coachHeaders(code)).then((r) => setJoueurs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/encadrement", coachHeaders(code)).then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    try {
      const { data } = await api.get("/fiches", coachHeaders(code));
      setFiches(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.some((x) => !x.vue)) {
        await api.post("/fiches/vues", {}, coachHeaders(code));
        onCount(0);
      }
    } catch { /* section facultative */ }
  }, [code, onCount]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); onIntegree(); } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); } finally { setBusy(false); }
  };
  const add = (fi) => act(() => api.post(`/fiches/${fi.id}/integrer`, {}, coachHeaders(code)), `${fi.prenom} ajouté(e) à l'effectif`);
  const del = (fi) => { if (window.confirm(`Supprimer la fiche de ${fi.prenom} ${fi.nom} ?`)) act(() => api.delete(`/fiches/${fi.id}`, coachHeaders(code)), "Fiche supprimée"); };
  const aTraiter = fiches.filter((x) => x.statut === "a_traiter");
  const faites = fiches.filter((x) => x.statut !== "a_traiter");

  return (
    <section data-testid="fiches-recues" className="mb-4 rounded-2xl border border-line bg-pitch p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-xl uppercase text-chalk"><ClipboardList className="h-5 w-5 text-lime" />Fiches reçues
          {aTraiter.length > 0 && <span data-testid="fiches-a-traiter-count" className="rounded-full bg-signal px-2 py-0.5 font-mono text-xs text-ink">{aTraiter.length} à ajouter</span>}
        </h2>
        <button type="button" data-testid="fiche-send-whatsapp" onClick={envoyerFiche} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"><MessageCircle className="h-4 w-4" />Envoyer la fiche sur WhatsApp</button>
      </div>
      <p className="mt-1 text-sm text-dim">Quand l'enfant est déjà dans l'effectif (même nom et prénom), sa fiche est recopiée automatiquement. Sinon, elle attend ici. Les fiches des encadrants sont recopiées directement.</p>
      {aTraiter.length > 0 && <ul className="mt-3 space-y-2">{aTraiter.map((fi) => <FicheRow key={fi.id} fi={fi} busy={busy} onAdd={add} onDelete={del} />)}</ul>}
      {faites.length > 0 && (
        <div className="mt-3">
          <button type="button" data-testid="fiches-history-toggle" onClick={() => setHisto((h) => !h)} className="inline-flex items-center gap-1 text-sm text-lime hover:underline">
            <ChevronDown className={`h-4 w-4 transition-transform ${histo ? "rotate-180" : ""}`} />{faites.length} fiche{faites.length > 1 ? "s" : ""} déjà recopiée{faites.length > 1 ? "s" : ""}
          </button>
          {histo && <ul className="mt-2 space-y-2">{faites.map((fi) => <FicheRow key={fi.id} fi={fi} busy={busy} onAdd={add} onDelete={del} />)}</ul>}
        </div>
      )}
      {fiches.length === 0 && <p data-testid="fiches-empty" className="mt-3 text-sm text-dim">Aucune fiche reçue pour le moment.</p>}
      <Manquantes joueurs={joueurs} code={code} onChange={() => { load(); onIntegree(); }} />
      <EncadrantsManquants staff={staff} />
    </section>
  );
};
